import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  CandidateDecisionUpdate,
  CandidateDetail,
  CandidateFilters,
  OrganizationLink,
  OrganizationLinkDetails,
  PaginatedResponse,
  PrivateBenchmarkUpsert,
  RankedCandidate,
  RecruitmentBenchmark,
  RecruitmentCandidate,
  RecruitmentDashboardData,
  RecruitmentOrganization,
} from '../recruitment/models/recruitment.models';
import {
  MOCK_BENCHMARKS,
  MOCK_CANDIDATES,
  MOCK_DASHBOARD,
  MOCK_RANKED_CANDIDATES,
  mockCandidateDetail,
} from '../recruitment/data/recruitment.mock-data';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class RecruitmentApiService {
  private readonly apiUrl = environment.recruitment.apiUrl;
  private readonly publicLinkUrl = environment.recruitment.publicLinkUrl;
  private readonly useMocks = environment.recruitment.useMocks;
  private organizationContextId: string | null = null;
  private mockDecisions = new Map<string, { state: RankedCandidate['decision']; note: string }>();
  private mockLink: OrganizationLink | null = MOCK_DASHBOARD.organizationLink;

  constructor(private http: HttpClient, private authService: AuthService) {}

  setOrganizationContext(orgId: string | null): void {
    this.organizationContextId = orgId;
  }

  getDashboard(): Observable<RecruitmentDashboardData> {
    if (this.useMocks) {
      return of({ ...MOCK_DASHBOARD, organizationLink: this.mockLink }).pipe(delay(250));
    }
    return this.http.get<{ dashboard: RecruitmentDashboardData }>(this.apiUrl, {
      params: this.toHttpParams('get-recruiter-dashboard', {}),
    }).pipe(map((response) => response.dashboard));
  }

  provisionRecruiterOrganization(
    recruiterUserId: string,
    organizationName: string,
    orgId?: string
  ): Observable<RecruitmentOrganization> {
    return this.http.post<{ organization: RecruitmentOrganization }>(this.apiUrl, {
      ...this.callerValues(),
      flow_selected: 'provision-recruiter-org',
      recruiterUserId,
      organizationName,
      ...(orgId ? { orgId } : {})
    }).pipe(map((response) => response.organization));
  }

  getRecruiterOrganization(recruiterUserId: string): Observable<RecruitmentOrganization> {
    return this.http.get<{ organization: RecruitmentOrganization }>(this.apiUrl, {
      params: this.toHttpParams('get-recruiter-organization', { recruiterUserId })
    }).pipe(map((response) => response.organization));
  }

  getOrganizationDetails(): Observable<RecruitmentOrganization> {
    return this.http.get<{ organization: RecruitmentOrganization }>(this.apiUrl, {
      params: this.toHttpParams('get-organization-details', {})
    }).pipe(map((response) => response.organization));
  }

  getBenchmarks(): Observable<RecruitmentBenchmark[]> {
    if (this.useMocks) return of(MOCK_BENCHMARKS).pipe(delay(180));
    return this.http.get<{ benchmarks: RecruitmentBenchmark[] }>(this.apiUrl, {
      params: this.toHttpParams('get-private-benchmarks', {}),
    }).pipe(map((response) => response.benchmarks || []));
  }

  upsertPrivateBenchmark(benchmark: PrivateBenchmarkUpsert): Observable<RecruitmentBenchmark> {
    return this.http.post<{ benchmark: RecruitmentBenchmark }>(this.apiUrl, {
      ...this.callerValues(),
      flow_selected: 'upsert-private-benchmark',
      ...benchmark
    }).pipe(map((response) => response.benchmark));
  }

  getCandidates(filters: CandidateFilters = {}): Observable<PaginatedResponse<RecruitmentCandidate>> {
    if (this.useMocks) {
      let candidates = [...MOCK_CANDIDATES];
      if (filters.search) {
        const search = filters.search.toLowerCase();
        candidates = candidates.filter((candidate) =>
          `${candidate.firstName} ${candidate.lastName} ${candidate.email}`.toLowerCase().includes(search)
        );
      }
      if (filters.assessment && filters.assessment !== 'ALL') {
        candidates = candidates.filter((candidate) => candidate.assessmentAvailability === filters.assessment);
      }
      return of({ items: candidates, total: candidates.length }).pipe(delay(220));
    }
    return this.http.get<PaginatedResponse<RecruitmentCandidate>>(this.apiUrl, {
      params: this.toHttpParams('get-org-candidates', filters),
    });
  }

  getRankedCandidates(
    benchmarkId: string,
    filters: CandidateFilters = {}
  ): Observable<PaginatedResponse<RankedCandidate>> {
    if (this.useMocks) {
      const benchmark = MOCK_BENCHMARKS.find((item) => item.benchmarkId === benchmarkId);
      let candidates = MOCK_RANKED_CANDIDATES.map((candidate, index) => {
        const decision = this.mockDecisions.get(`${benchmarkId}:${candidate.userId}`);
        const shortfallAdjustment = benchmarkId === candidate.fit.benchmarkId ? 0 : 5 + index * 1.5;
        return {
          ...candidate,
          decision: decision?.state || candidate.decision,
          note: decision?.note ?? candidate.note,
          fit: {
            ...candidate.fit,
            benchmarkId,
            roleTitle: benchmark?.roleTitle || candidate.fit.roleTitle,
            matchScore: Math.max(62, Math.round(candidate.fit.matchScore - shortfallAdjustment)),
            averageShortfall: candidate.fit.averageShortfall + shortfallAdjustment,
          },
        };
      }).sort((a, b) => a.fit.averageShortfall - b.fit.averageShortfall);

      if (filters.search) {
        const search = filters.search.toLowerCase();
        candidates = candidates.filter((candidate) =>
          `${candidate.firstName} ${candidate.lastName} ${candidate.email}`.toLowerCase().includes(search)
        );
      }
      if (filters.decision && filters.decision !== 'ALL') {
        candidates = candidates.filter((candidate) => candidate.decision === filters.decision);
      }
      if (filters.behavioral && filters.behavioral !== 'ALL') {
        candidates = candidates.filter((candidate) => candidate.fit.behavioralAlignment === filters.behavioral);
      }
      return of({ items: candidates, total: candidates.length }).pipe(delay(260));
    }

    return this.http.get<PaginatedResponse<RankedCandidate>>(this.apiUrl, {
      params: this.toHttpParams('get-ranked-candidates', { ...filters, benchmarkId }),
    });
  }

  getCandidateDetail(candidateId: string): Observable<CandidateDetail> {
    if (this.useMocks) {
      const candidate = mockCandidateDetail(candidateId);
      return candidate ? of(candidate).pipe(delay(220)) : throwError(() => new Error('Candidate not found'));
    }
    return this.http.get<{ candidate: CandidateDetail }>(this.apiUrl, {
      params: this.toHttpParams('get-candidate-details', { candidateId }),
    }).pipe(map((response) => response.candidate));
  }

  updateCandidateDecision(update: CandidateDecisionUpdate): Observable<void> {
    if (this.useMocks) {
      this.mockDecisions.set(`${update.benchmarkId}:${update.candidateId}`, {
        state: update.state,
        note: update.note || '',
      });
      return of(undefined).pipe(delay(180));
    }
    return this.http.post<void>(this.apiUrl, {
      ...this.callerValues(),
      flow_selected: 'update-candidate-state',
      benchmarkId: update.benchmarkId,
      candidateId: update.candidateId,
      state: update.state,
      note: update.note || '',
    });
  }

  createOrganizationLink(): Observable<OrganizationLink> {
    if (this.useMocks) {
      this.mockLink = {
        inviteId: `invite-${Date.now()}`,
        url: `${window.location.origin}/join/northstar-demo`,
        expiresAt: new Date(Date.now() + 30 * 86400000).toISOString(),
        joinCount: 0,
        revoked: false,
      };
      return of(this.mockLink).pipe(delay(180));
    }
    return this.http.post<{ organizationLink: OrganizationLink }>(this.apiUrl, {
      ...this.callerValues(),
      flow_selected: 'create-org-link',
    }).pipe(map((response) => response.organizationLink));
  }

  revokeOrganizationLink(): Observable<void> {
    if (this.useMocks) {
      this.mockLink = this.mockLink ? { ...this.mockLink, revoked: true } : null;
      return of(undefined).pipe(delay(180));
    }
    return this.http.post<void>(this.apiUrl, {
      ...this.callerValues(),
      flow_selected: 'revoke-org-link'
    });
  }

  getOrganizationLinkDetails(token: string): Observable<OrganizationLinkDetails> {
    if (this.useMocks) {
      return of({
        organizationName: MOCK_DASHBOARD.organization.name,
        expiresAt: this.mockLink?.expiresAt || new Date(Date.now() + 30 * 86400000).toISOString(),
        revoked: false,
      }).pipe(delay(180));
    }
    return this.http.get<OrganizationLinkDetails>(this.publicLinkUrl, {
      params: { token },
    });
  }

  claimOrganizationLink(token: string): Observable<void> {
    if (this.useMocks) return of(undefined).pipe(delay(250));
    return this.http.post<void>(this.apiUrl, {
      ...this.callerValues(),
      flow_selected: 'claim-org-link',
      token
    });
  }

  getResumeUrl(candidateId: string): Observable<string> {
    if (this.useMocks) {
      const mockResume = new Blob([
        'Konvac local preview\n\nA private signed resume URL will be returned by the recruitment backend.',
      ], { type: 'text/plain' });
      return of(URL.createObjectURL(mockResume)).pipe(delay(120));
    }
    return this.http.get<{ resumeUrl: string }>(this.apiUrl, {
      params: this.toHttpParams('get-resume-url', { candidateId }),
    }).pipe(map((response) => response.resumeUrl));
  }

  private toHttpParams(queryType: string, values: Record<string, any>): HttpParams {
    let params = new HttpParams().set('queryType', queryType);
    Object.entries({ ...this.callerValues(), ...values }).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== 'ALL') {
        params = params.set(key, String(value));
      }
    });
    return params;
  }

  private callerValues(): Record<string, string> {
    const user = this.authService.getCurrentUser();
    const caller = user?.userId
      ? { userId: user.userId, role: user.role }
      : {};
    return this.organizationContextId
      ? { ...caller, orgId: this.organizationContextId }
      : caller;
  }
}
